/**
 * The shared ledger: how a booking on one device becomes safe when another
 * device books the same company through the same cloud folder.
 *
 * Verifikation numbers are gapless per fiscal year and series, and a
 * verifikation can never be rewritten. Two devices that each number "the next
 * one" locally will therefore both mint A13, and no later merge can undo
 * that. So when the company lives in a store that offers a conditional write
 * (Koofr: `overwriteIfModified`), the remote `bokforing.json` becomes the
 * point of serialisation and every write to the ledger is an optimistic lock:
 *
 *   1. pull  — bring the local copy up to date with the remote snapshot;
 *   2. write — run the flow's own transaction locally, as always;
 *   3. push  — before that transaction commits, upload the snapshot with
 *              "only if the remote is still the version I pulled";
 *   4. commit — only when the push succeeded. A rejected push aborts the
 *              transaction, the newer remote copy is merged, and the flow
 *              runs again with fresh numbers.
 *
 * The domain owns the order of those steps; the cloud layer owns what they
 * do. `src/domain` may not import `src/cloud`, so the cloud layer registers
 * the three operations here at start-up (`installSharedLedger` in
 * `src/cloud/sync/ledger.ts`, called from `src/wiring.ts`). With nothing
 * registered — tests, or a store without conditional writes — the flow runs
 * exactly as before: locally, and the backup scheduler copies it later.
 *
 * Every flow that writes the legal record (`bookVerifikation` and the
 * compound flows around it) goes through `withSharedLedger`. Nested calls
 * are the outer flow's business: a `bookVerifikation` inside the year-end
 * transaction must not open a second lock or a second push.
 */
import Dexie from 'dexie';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { Uuid } from '../types';

/**
 * Every table the snapshot of a company is built from. A flow under the
 * shared ledger opens its transaction over all of them, because the push
 * exports the company from inside that transaction — it has to see the rows
 * the flow just wrote — and Dexie only lets a transaction read tables it
 * declared. Not the app-wide `settings`, `driveState`, `backupLog`: the push
 * writes those in transactions of its own while this one waits.
 */
export const SHARED_LEDGER_TABLES = [
  'companies',
  'companySettings',
  'fiscalYears',
  'accounts',
  'accountEvents',
  'verifikationer',
  'paymentSources',
  'merchantRules',
  'periodLocks',
  'bankStatements',
  'bankRows',
  'inboxItems',
  'attachments',
  'devices',
] as const;

/** How many times a flow is rerun after the remote moved under it. */
export const MAX_LEDGER_ATTEMPTS = 3;

export interface SharedLedgerHooks {
  /** True when this company's writes must be serialised through the remote copy. */
  isShared(companyId: Uuid, db: BookkeepingDB): Promise<boolean>;
  /**
   * Merge whatever another device pushed since our copy last agreed with the
   * remote. Rejects with `LedgerOfflineError` when the remote cannot be
   * reached and `LedgerConflictError` when the two copies collide.
   */
  pull(companyId: Uuid, db: BookkeepingDB): Promise<void>;
  /**
   * Upload the company as it looks inside the calling transaction, on the
   * condition that the remote is still what `pull` saw. Rejects with
   * `LedgerMovedError` when it is not; the transaction then rolls back.
   */
  publish(companyId: Uuid, db: BookkeepingDB): Promise<void>;
  /** Mutual exclusion per company across tabs, so two flows never interleave their pull and push. */
  lock<T>(companyId: Uuid, db: BookkeepingDB, fn: () => Promise<T>): Promise<T>;
}

let hooks: SharedLedgerHooks | null = null;

/** Registered once at start-up by the cloud layer; null means "book locally". */
export function configureSharedLedger(next: SharedLedgerHooks | null): void {
  hooks = next;
}

export function sharedLedgerHooks(): SharedLedgerHooks | null {
  return hooks;
}

// ---- Errors --------------------------------------------------------------------

/**
 * Internal signal: the remote snapshot changed between pull and push. The
 * flow is rerun; a user only sees it as `LedgerRetryError` when it keeps
 * happening.
 */
export class LedgerMovedError extends Error {
  constructor(message = 'Bokföringen ändrades på en annan enhet under tiden.') {
    super(message);
    this.name = 'LedgerMovedError';
  }
}

export class LedgerOfflineError extends Error {
  constructor(provider: string) {
    super(
      `Ingen kontakt med ${provider}. Bokföringen delas mellan flera enheter, så en händelse kan bara bokföras när ${provider} går att nå. Kontrollera nätverket och försök igen.`,
    );
    this.name = 'LedgerOfflineError';
  }
}

export class LedgerRetryError extends Error {
  constructor(provider: string) {
    super(`Bokföringen ändrades på en annan enhet flera gånger i rad, så den här händelsen fick vänta. Ändringarna från ${provider} är hämtade – försök igen.`);
    this.name = 'LedgerRetryError';
  }
}

export interface NumberCollision {
  fiscalYearId: Uuid;
  series: string;
  number: number;
  localId: Uuid;
  remoteId: Uuid;
}

/** "A13, A14" — the colliding numbers, for messages and the log. */
export function collisionLabels(collisions: readonly NumberCollision[]): string[] {
  return [...new Set(collisions.map((c) => `${c.series}${c.number}`))];
}

/**
 * The two copies booked the same number. Nothing can be merged or pushed
 * until the user picks which copy stands (Mer › Lagring › <provider>).
 */
export class LedgerConflictError extends Error {
  constructor(
    public readonly collisions: readonly NumberCollision[],
    provider: string,
  ) {
    const labels = collisionLabels(collisions);
    super(
      `Den här enheten och en annan enhet har bokfört samma nummer (${labels.join(', ')}). Ingen av dem skrivs över automatiskt – välj under Mer › Lagring › ${provider} vilken enhets bokföring som ska gälla.`,
    );
    this.name = 'LedgerConflictError';
  }
}

export function isLedgerError(e: unknown): e is LedgerOfflineError | LedgerRetryError | LedgerConflictError {
  return e instanceof LedgerOfflineError || e instanceof LedgerRetryError || e instanceof LedgerConflictError;
}

/**
 * An error from the cloud store (`RemoteStoreError` and its adapters'
 * subclasses), recognised by shape: the domain cannot import `src/cloud`, but
 * a booking under the shared ledger can fail on the network, and the store's
 * own Swedish sentence names the provider and the next step.
 */
export function isProviderError(e: unknown): e is Error & { provider: string; technical: string } {
  return e instanceof Error && typeof (e as { provider?: unknown }).provider === 'string' && typeof (e as { technical?: unknown }).technical === 'string';
}

/**
 * What to show when a booking failed: the ledger's or the provider's own
 * sentence when the failure is theirs, else the caller's generic one.
 */
export function bookingFailureMessage(e: unknown, fallback: string): string {
  if (isLedgerError(e) || isProviderError(e)) return e.message;
  return fallback;
}

// ---- The wrapper ---------------------------------------------------------------

/**
 * Run `work` — a flow that writes the company's ledger — under the shared
 * ledger when the company has one, and plainly otherwise.
 *
 * Under the shared ledger the flow's own transaction becomes a
 * sub-transaction of one opened here over every company table, so the
 * publish step can export the company with the flow's writes included. A
 * `LedgerMovedError` from the publish aborts that transaction (Dexie rolls
 * back on a rejection), the newer remote copy is merged, and the whole flow
 * runs again — numbers, validation and all — up to `MAX_LEDGER_ATTEMPTS`.
 *
 * Called from inside another transaction, it does nothing but run `work`:
 * the outer flow is the one holding the lock and doing the push.
 */
export async function withSharedLedger<T>(companyId: Uuid, db: BookkeepingDB = defaultDb, work: () => Promise<T>): Promise<T> {
  const active = hooks;
  if (!active || Dexie.currentTransaction) return work();
  if (!(await active.isShared(companyId, db))) return work();
  return active.lock(companyId, db, async () => {
    let provider = 'lagringen';
    for (let attempt = 1; ; attempt++) {
      await active.pull(companyId, db);
      try {
        return await db.transaction(
          'rw',
          SHARED_LEDGER_TABLES.map((name) => db.table(name)),
          async () => {
            const result = await work();
            await active.publish(companyId, db);
            return result;
          },
        );
      } catch (e) {
        if (!(e instanceof LedgerMovedError)) throw e;
        provider = providerOf(e) ?? provider;
        if (attempt >= MAX_LEDGER_ATTEMPTS) throw new LedgerRetryError(provider);
      }
    }
  });
}

function providerOf(e: LedgerMovedError): string | null {
  const provider = (e as { provider?: unknown }).provider;
  return typeof provider === 'string' && provider ? provider : null;
}
